// Geo-tagged ImageObject JSON-LD builder.
// Use on every page that has a primary/share image so search engines and
// AI crawlers can attribute the image to a specific city/ZIP — strengthens
// local relevance for image search and AI grounding.

export interface GeoImageOptions {
  url: string;
  /** Primary keyword + city, e.g. "Aurora SEO Agency — Colorado Web Impressions" */
  caption: string;
  /** Plain language description; falls back to caption */
  description?: string;
  /** City the page targets (used for contentLocation City node) */
  cityName?: string;
  /** ZIP codes the page targets — emitted as PostalCodeArea contentLocations */
  zips?: string[];
  /** Lat/lng of the targeted area (city centroid, not HQ) */
  geo?: { lat: number; lng: number };
  /** Optional explicit width/height */
  width?: number;
  height?: number;
}

const SITE = "https://coloradowebimpressions.com";

export function geoImageObject(opts: GeoImageOptions): Record<string, unknown> {
  const node: Record<string, unknown> = {
    "@type": "ImageObject",
    url: opts.url,
    contentUrl: opts.url,
    caption: opts.caption,
    description: opts.description ?? opts.caption,
    creator: { "@id": `${SITE}/#org` },
    copyrightHolder: { "@id": `${SITE}/#org` },
    license: `${SITE}/terms`,
    acquireLicensePage: `${SITE}/contact`,
  };
  if (opts.width) node.width = opts.width;
  if (opts.height) node.height = opts.height;

  // contentLocation: stack City, PostalCodeArea(s), GeoCoordinates so crawlers
  // see structured local attribution attached to the image itself.
  const locations: Array<Record<string, unknown>> = [];
  if (opts.cityName) {
    locations.push({
      "@type": "City",
      name: opts.cityName,
      containedInPlace: { "@type": "State", name: "Colorado" },
      ...(opts.geo
        ? {
            geo: {
              "@type": "GeoCoordinates",
              latitude: opts.geo.lat,
              longitude: opts.geo.lng,
            },
          }
        : {}),
    });
  }
  if (opts.zips && opts.zips.length) {
    for (const z of opts.zips) {
      locations.push({
        "@type": "PostalCodeArea",
        postalCode: z,
        addressRegion: "CO",
        addressCountry: "US",
      });
    }
  }
  if (locations.length === 1) {
    node.contentLocation = locations[0];
  } else if (locations.length > 1) {
    node.contentLocation = locations;
  }

  // exifData GPS — gives image-search crawlers latitude/longitude directly on
  // the ImageObject (no need to read EXIF from the binary).
  if (opts.geo) {
    node.exifData = [
      { "@type": "PropertyValue", name: "GPSLatitude", value: String(opts.geo.lat) },
      { "@type": "PropertyValue", name: "GPSLongitude", value: String(opts.geo.lng) },
    ];
  }

  return node;
}
